import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusCircle, Edit2, Trash2, Save, X, ChevronDown, Loader2,
  HelpCircle, GripVertical, ChevronUp, Eye, EyeOff, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

/* ─────────────────────────── Modal ─────────────────────────── */
function FAQModal({ faq, categories, onClose, onSaved }) {
  const isEdit = Boolean(faq?.id);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    question: faq?.question || '',
    answer: faq?.answer || '',
    category: faq?.category || 'General',
    sort_order: faq?.sort_order ?? 0,
    is_active: faq?.is_active !== false,
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/api/marketing/faqs/${faq.id}`
        : `${API}/api/marketing/faqs`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ ...form, sort_order: parseInt(form.sort_order) || 0 }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'FAQ updated' : 'FAQ created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  const inputCls =
    'w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-colors';
  const labelCls = 'block text-sm font-semibold text-slate-700 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
              <HelpCircle size={16} className="text-teal-600" />
            </div>
            <h2 className="text-base font-bold text-slate-900">
              {isEdit ? 'Edit FAQ' : 'New FAQ'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Question */}
          <div>
            <label className={labelCls}>Question <span className="text-red-400">*</span></label>
            <textarea
              value={form.question}
              onChange={set('question')}
              rows={2}
              required
              placeholder="e.g. How does ShiftCore handle NDIS billing?"
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Answer */}
          <div>
            <label className={labelCls}>Answer <span className="text-red-400">*</span></label>
            <textarea
              value={form.answer}
              onChange={set('answer')}
              rows={5}
              required
              placeholder="Provide a clear, helpful answer..."
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Category */}
          <div>
            <label className={labelCls}>Category</label>
            <input
              type="text"
              list="faq-categories"
              value={form.category}
              onChange={set('category')}
              placeholder="e.g. Billing, NDIS, Getting Started"
              className={inputCls}
            />
            <datalist id="faq-categories">
              {categories.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
            <p className="text-xs text-slate-400 mt-1">Type to create a new category or select an existing one</p>
          </div>

          {/* Sort order */}
          <div>
            <label className={labelCls}>Sort Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={set('sort_order')}
              min={0}
              className={inputCls}
            />
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
            <div className="relative">
              <input
                type="checkbox"
                id="faq_active"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-9 h-5 rounded-full transition-colors',
                  form.is_active ? 'bg-teal-500' : 'bg-slate-300'
                )}
              />
              <div
                className={clsx(
                  'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                  form.is_active ? 'translate-x-4' : 'translate-x-0'
                )}
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">
                {form.is_active ? 'Active — shown on website' : 'Hidden — not shown on website'}
              </p>
              <p className="text-xs text-slate-400">Toggle to show or hide this FAQ from visitors</p>
            </div>
          </label>

          {/* Footer */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create FAQ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────── Accordion Item ─────────────────────────── */
function FAQItem({ faq, isFirst, isLast, onEdit, onDelete, onToggleActive, onMoveUp, onMoveDown }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={clsx(
        'bg-white border rounded-xl overflow-hidden transition-all',
        faq.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60',
        open && 'shadow-sm'
      )}
    >
      {/* Question row */}
      <div className="flex items-center gap-1">
        {/* Drag handle */}
        <div className="pl-3 py-4 text-slate-300 cursor-grab active:cursor-grabbing shrink-0">
          <GripVertical size={16} />
        </div>

        {/* Question button */}
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex-1 flex items-center justify-between px-3 py-4 text-left hover:bg-slate-50/60 transition-colors min-w-0"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {!faq.is_active && (
              <span className="shrink-0 text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                Hidden
              </span>
            )}
            <span className="text-sm font-semibold text-slate-900 leading-snug">{faq.question}</span>
          </div>
          <ChevronDown
            size={15}
            className={clsx(
              'text-slate-400 transition-transform duration-200 shrink-0 ml-3',
              open && 'rotate-180'
            )}
          />
        </button>

        {/* Inline actions */}
        <div className="flex items-center gap-1 pr-3 shrink-0">
          <button
            onClick={() => onMoveUp(faq)}
            disabled={isFirst}
            title="Move up"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-25"
          >
            <ChevronUp size={13} />
          </button>
          <button
            onClick={() => onMoveDown(faq)}
            disabled={isLast}
            title="Move down"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-25"
          >
            <ChevronDown size={13} />
          </button>
          <button
            onClick={() => onToggleActive(faq)}
            title={faq.is_active ? 'Hide FAQ' : 'Show FAQ'}
            className={clsx(
              'p-1.5 rounded-lg transition-colors',
              faq.is_active
                ? 'text-teal-500 hover:text-teal-700 hover:bg-teal-50'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            )}
          >
            {faq.is_active ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
          <button
            onClick={() => onEdit(faq)}
            title="Edit"
            className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
          >
            <Edit2 size={13} />
          </button>
          <button
            onClick={() => onDelete(faq.id)}
            title="Delete"
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Answer panel */}
      {open && (
        <div className="px-5 pb-4 pt-3 border-t border-slate-100 bg-slate-50/50">
          <p className="text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Category Group ─────────────────────────── */
function CategoryGroup({ category, faqs, onEdit, onDelete, onToggleActive, onMoveUp, onMoveDown }) {
  return (
    <div>
      {/* Category header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1 h-5 bg-teal-500 rounded-full" />
        <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">{category}</h3>
        <span className="text-[10px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
          {faqs.length}
        </span>
      </div>

      {/* FAQ items */}
      <div className="space-y-2 ml-4">
        {faqs.map((faq, idx) => (
          <FAQItem
            key={faq.id}
            faq={faq}
            isFirst={idx === 0}
            isLast={idx === faqs.length - 1}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleActive={onToggleActive}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── Main Page ──────────────────────────────── */
export default function FAQs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | faq object
  const [filterCategory, setFilterCategory] = useState('All');
  const token = localStorage.getItem('token');

  async function fetchFaqs() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/faqs/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setFaqs(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchFaqs(); }, []);

  async function deleteFaq(id) {
    if (!window.confirm('Delete this FAQ? This cannot be undone.')) return;
    try {
      await fetch(`${API}/api/marketing/faqs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch {
      toast.error('Failed to delete FAQ');
    }
  }

  async function toggleActive(faq) {
    try {
      await fetch(`${API}/api/marketing/faqs/${faq.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...faq, is_active: !faq.is_active }),
      });
      toast.success(faq.is_active ? 'FAQ hidden' : 'FAQ shown');
      fetchFaqs();
    } catch {
      toast.error('Failed to update FAQ');
    }
  }

  async function moveFaq(faq, direction) {
    // direction: -1 = up, +1 = down
    const catFaqs = sorted.filter((f) => f.category === faq.category);
    const idx = catFaqs.findIndex((f) => f.id === faq.id);
    const target = catFaqs[idx + direction];
    if (!target) return;
    try {
      await Promise.all([
        fetch(`${API}/api/marketing/faqs/${faq.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...faq, sort_order: target.sort_order }),
        }),
        fetch(`${API}/api/marketing/faqs/${target.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...target, sort_order: faq.sort_order }),
        }),
      ]);
      fetchFaqs();
    } catch {
      toast.error('Failed to reorder');
    }
  }

  const categories = [...new Set(faqs.map((f) => f.category))].filter(Boolean).sort();
  const sorted = [...faqs].sort((a, b) => a.sort_order - b.sort_order);
  const filtered = filterCategory === 'All' ? sorted : sorted.filter((f) => f.category === filterCategory);

  // Group by category for display
  const grouped = categories.reduce((acc, cat) => {
    const items = filtered.filter((f) => f.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  // Items without a matched category (when filter is "All")
  const ungrouped = filtered.filter((f) => !categories.includes(f.category));

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {modal !== null && (
        <FAQModal
          faq={modal === 'new' ? null : modal}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={fetchFaqs}
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">FAQs</h1>
            <span className="px-2.5 py-0.5 text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full">
              {faqs.length} total
            </span>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Questions shown in the FAQ section of the marketing website
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/#faqs`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm"
          >
            <PlusCircle size={16} />
            Add FAQ
          </button>
        </div>
      </div>

      {/* Category filter bar */}
      {categories.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {['All', ...categories].map((cat) => {
            const count = cat === 'All' ? faqs.length : faqs.filter((f) => f.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={clsx(
                  'flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all',
                  filterCategory === cat
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-teal-300 hover:text-teal-700'
                )}
              >
                {cat}
                <span
                  className={clsx(
                    'text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                    filterCategory === cat ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
          <HelpCircle size={40} className="mb-3 opacity-30" />
          <p className="font-semibold text-slate-500">No FAQs found</p>
          <p className="text-sm mt-1">
            {filterCategory === 'All' ? 'Add your first FAQ with the button above' : `No FAQs in "${filterCategory}"`}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <CategoryGroup
              key={cat}
              category={cat}
              faqs={items}
              onEdit={setModal}
              onDelete={deleteFaq}
              onToggleActive={toggleActive}
              onMoveUp={(faq) => moveFaq(faq, -1)}
              onMoveDown={(faq) => moveFaq(faq, 1)}
            />
          ))}
          {ungrouped.length > 0 && (
            <CategoryGroup
              category="Uncategorised"
              faqs={ungrouped}
              onEdit={setModal}
              onDelete={deleteFaq}
              onToggleActive={toggleActive}
              onMoveUp={(faq) => moveFaq(faq, -1)}
              onMoveDown={(faq) => moveFaq(faq, 1)}
            />
          )}
        </div>
      )}
    </div>
  );
}
